import { Link } from "react-router-dom";
import { m } from "framer-motion";
import { services } from "@/lib/services-data";
import {
  ArrowRight,
  Home,
  Building2,
  BrickWall,
  PaintRoller,
  HardHat,
  Layers,
  Fence,
  Grid3x3,
  LayoutGrid,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import ResponsiveImage from "@/components/ResponsiveImage";

const serviceIcons: Record<string, typeof Home> = {
  "home-remodeling": Home,
  painting: PaintRoller,
  masonry: BrickWall,
  roofing: Layers,
  "retaining-walls": Fence,
  pavers: Grid3x3,
  flooring: LayoutGrid,
  "new-construction": Building2,
  "general-construction": HardHat,
};

const ServicesSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section id="services" className="relative -mt-px section-padding section-gradient-soft">
      <div className="container-max">
        <div className="text-center mb-14 md:mb-16">
          <m.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-primary-deep text-sm font-bold uppercase tracking-widest mb-3"
          >
            What We Offer
          </m.span>
          <m.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground mb-4 tracking-tight font-bold"
          >
            Our <span className="text-gradient-primary">Services</span>
          </m.h2>
          <m.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-muted-foreground max-w-2xl mx-auto text-lg"
          >
            Whether you're remodeling your home, improving your property's exterior, or planning a
            larger build, we cover the trades that get it finished — across Clifton and the
            surrounding New Jersey communities.
          </m.p>
        </div>

        {/* 3-col grid — nine services fill it exactly */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {services.map((service, i) => {
            const Icon = serviceIcons[service.slug] || Home;

            return (
              <m.div
                key={service.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="w-full flex"
              >
                <div
                  className="group relative rounded-2xl w-full flex flex-col card-hover-lift bg-card border border-border shadow-sm hover:shadow-2xl overflow-hidden isolate"
                >
                  <Link to={`/services/${service.slug}`} className="relative aspect-[4/3] w-full block overflow-hidden">
                    <ResponsiveImage
                      src={service.image}
                      sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                      alt={service.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                      width={1400}
                      height={1050}
                      decoding="async"
                    />
                    <div className="absolute inset-0 overlay-gradient" />

                    <div className="absolute top-4 left-4 z-10">
                      <div className="h-12 w-12 rounded-xl bg-primary/95 flex items-center justify-center backdrop-blur-sm shadow-lg">
                        <Icon className="h-6 w-6 text-primary-foreground" />
                      </div>
                    </div>

                    <div className="absolute top-4 right-4 z-10">
                      <span className="inline-block bg-secondary text-secondary-foreground text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-md">
                        {service.price}
                      </span>
                    </div>

                    <div className="relative z-10 flex flex-col justify-end h-full p-6">
                      <h3 className="font-heading text-2xl text-white mb-1 tracking-tight font-bold drop-shadow-md">
                        {service.title}
                      </h3>
                      <p className="text-white/85 text-sm">{service.subtitle}</p>
                    </div>
                  </Link>

                  <div className="mt-auto p-5 bg-card border-t border-border">
                    <Link
                      to={`/services/${service.slug}`}
                      className="inline-flex items-center gap-2 text-primary-deep font-semibold text-sm hover:gap-3 transition-all"
                    >
                      Learn More<span className="sr-only"> about {service.title}</span>{" "}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </m.div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-3 mt-12">
          <Button size="lg" onClick={openQuoteForm} className="gap-2 rounded-xl font-semibold">
            Get Free Estimate <ArrowRight className="h-5 w-5" />
          </Button>
          <Button asChild size="lg" variant="outline" className="gap-2 rounded-xl font-semibold w-full">
            <Link to="/services">
              View All Services
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
